'use client';

import { isPlainObject } from 'es-toolkit/predicate';
import { cn } from '@/lib/utils';
import { formatDisplayValue, formatLabel, getArrayItemKey } from '../utils';

export function ToolError({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
      {message}
    </div>
  );
}

export function SimpleResult({ text }: { text: string }) {
  return <div className="rounded-md border border-border/50 bg-card/80 p-2 text-sm text-foreground/90">{text}</div>;
}

export function EmptyResult({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-border/50 bg-muted/40 p-2 text-xs text-muted-foreground">{message}</div>
  );
}

export function GenericObjectResult({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <EmptyResult message="No fields returned." />;
  }
  return <KeyValueTable data={data} />;
}

export function GenericArrayResult({ items }: { items: unknown[] }) {
  if (items.length === 0) {
    return <EmptyResult message="No items returned." />;
  }

  if (items.every((item): item is Record<string, unknown> => isPlainObject(item))) {
    const objectItems = items.filter((item): item is Record<string, unknown> => isPlainObject(item));
    return (
      <div className="space-y-2">
        {objectItems.map((item, index) => (
          <KeyValueTable key={getArrayItemKey(item, index)} data={item} />
        ))}
      </div>
    );
  }

  return (
    <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/90">
      {items.map((item, index) => (
        <li key={getArrayItemKey(item, index)}>{formatDisplayValue(item)}</li>
      ))}
    </ul>
  );
}

export function KeyValueTable({ data, className }: { data: Record<string, unknown>; className?: string }) {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <EmptyResult message="No fields provided." />;
  }

  return (
    <div className={cn('rounded-md border border-border/50 p-2', className)}>
      <dl className="grid gap-1 text-xs">
        {entries.map(([key, value]) => (
          <div key={key} className="grid gap-2 sm:grid-cols-[max(120px,_30%)_1fr]">
            <dt className="break-words font-medium text-muted-foreground">{formatLabel(key)}</dt>
            <dd className="break-words text-foreground/90">{formatDisplayValue(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
